import {
   PIXEL_EVENTS,
   PIXEL_SCRIPT_URL,
   PIXEL_STEPS,
   deletePixelCookies,
   isPixelAddressClean,
   isPixelReferrerClean,
   normalisePixelId,
   pixelEmailHash,
   pixelExternalIdHash,
   pixelStepEventName,
   readPixelFbp,
   sanitisePixelParams,
   type PixelStep,
   type PixelUser,
} from '~/utils/pixelCore';
import { pauseGtm, setGtmAllowed, stopGtmForMinor } from '~/composables/useGoogleTagManager';

// The Meta Pixel. The script is not loaded, and no cookie is set, until someone has
// called setPixelAllowed(true); after that an event is sent only if the rules below
// all hold. A page view is sent only through notePixelPageView.

const SENT_KEY = 'talktime.pixel.sent.v1';
const MINOR_KEY = 'talktime.pixel.minor.v1';
const HELD_LIMIT = 20;

type LoadState = 'idle' | 'loading' | 'ready' | 'failed';
type Item = {
   name: string;
   params: Record<string, string | number>;
   eventID?: string;
   key: string;
   onSent?: () => void;
};

let allowed = false;
let loadState: LoadState = 'idle';
let revoked = false;
let paused = false;
let identityStale = false;
let libraryExternalId = '';
let minorStopped = false;
let routeHasParams = false;
let user: PixelUser | null = null;
let userKey = '';
let held: Item[] = [];
const sentKeys = new Set<string>();
let sentLoaded = false;
// The address of the page whose view is still to be sent. Each finished navigation
// replaces it, so a view is never sent from, or for, any page but its own.
let pageView: string | null = null;
// Whether the page the person is on has had its view, however its query changed since.
let pageViewSent = false;

const pixelId = () => {
   try {
      return normalisePixelId(useRuntimeConfig().public.META_PIXEL_ID);
   } catch {
      return '';
   }
};

const isMinorStopped = () => {
   if (minorStopped) return true;
   try {
      if (localStorage.getItem(MINOR_KEY) === '1') minorStopped = true;
   } catch {
      // Storage can be unavailable; the in-memory flag still holds.
   }
   return minorStopped;
};

const loadSent = () => {
   if (sentLoaded) return;
   sentLoaded = true;
   try {
      const saved: unknown = JSON.parse(sessionStorage.getItem(SENT_KEY) || '[]');
      if (Array.isArray(saved)) for (const key of saved) if (typeof key === 'string') sentKeys.add(key);
   } catch {
      // Without storage, once-only holds for this page load only.
   }
};

const markSent = (key: string) => {
   if (!key) return;
   loadSent();
   sentKeys.add(key);
   try {
      sessionStorage.setItem(SENT_KEY, JSON.stringify([...sentKeys]));
   } catch {
      // See loadSent.
   }
};

const isActive = () => !!pixelId() && allowed && !paused && !identityStale && !isMinorStopped();

const fbq = () => (window as any).fbq;

const addressIsClean = () =>
   isPixelAddressClean(
      { pathname: location.pathname, search: location.search, hash: location.hash },
      routeHasParams,
   ) && isPixelReferrerClean(document.referrer, location.origin);

const initArgs = (): unknown[] => {
   const id = pixelId();
   if (!user) return ['init', id];
   return ['init', id, user.em ? { em: user.em, external_id: user.external_id } : { external_id: user.external_id }];
};

const callFbq = (...args: unknown[]) => {
   try {
      fbq()?.(...args);
   } catch {
      // A third-party script must never break the page.
   }
};

// True only when the real library, not the queue stub, took the call.
const emit = (item: Item): boolean => {
   const library = fbq();
   if (loadState !== 'ready' || typeof library?.callMethod !== 'function') return false;
   const rule = PIXEL_EVENTS[item.name];
   try {
      if (item.eventID) library(rule.method, item.name, item.params, { eventID: item.eventID });
      else library(rule.method, item.name, item.params);
   } catch {
      return false;
   }
   markSent(item.key);
   item.onSent?.();
   return true;
};

const currentAddress = () => `${location.pathname}${location.search}${location.hash}`;

// Not an entry in PIXEL_EVENTS and never held: a view belongs to one page, and a
// yes or a library that arrives while the person is still on it sends it then.
// The library is told not to report page changes itself (disablePushState), so
// this is the only page view it sends.
const sendPageView = () => {
   if (pageView === null) return;
   if (pageView !== currentAddress()) {
      pageView = null;
      return;
   }
   const library = fbq();
   if (!isActive() || loadState !== 'ready' || typeof library?.callMethod !== 'function') return;
   if (!addressIsClean()) return;
   pageView = null;
   pageViewSent = true;
   try {
      library('track', 'PageView');
   } catch {
      // A third-party script must never break the page.
   }
};

const flushHeld = () => {
   sendPageView();
   if (!isActive() || loadState !== 'ready' || !addressIsClean()) return;
   const waiting = held;
   held = [];
   for (const item of waiting) {
      loadSent();
      if (item.key && sentKeys.has(item.key)) continue;
      emit(item);
   }
};

const startLoad = () => {
   if (loadState !== 'idle') return;
   loadState = 'loading';
   const stub: any = function (...args: unknown[]) {
      if (stub.callMethod) stub.callMethod(...args);
      else stub.queue.push(args);
   };
   stub.push = stub;
   stub.loaded = true;
   stub.version = '2.0';
   stub.queue = [];
   // The library must not report page changes on its own: addresses in the hub can
   // name a session, a journal or a therapist.
   stub.disablePushState = true;
   // Without this the library sends only the first PageView of a page load and
   // silently drops the rest; sendPageView already sends one per page at most.
   stub.allowDuplicatePageViews = true;
   (window as any).fbq = stub;
   if (!(window as any)._fbq) (window as any)._fbq = stub;
   stub('set', 'autoConfig', false, pixelId());
   stub(...initArgs());
   userKey = user ? `${user.em ?? ''}:${user.external_id}` : '';
   libraryExternalId = user?.external_id ?? '';
   const script = document.createElement('script');
   script.async = true;
   script.src = PIXEL_SCRIPT_URL;
   script.onload = () => {
      loadState = typeof fbq()?.callMethod === 'function' ? 'ready' : 'failed';
      if (loadState === 'failed') held = [];
      else flushHeld();
      // A no that came while the script was loading: its start-up may have set the cookie.
      if (!allowed) deleteCookies();
   };
   script.onerror = () => {
      loadState = 'failed';
      held = [];
   };
   (document.head || document.documentElement).appendChild(script);
};

const deleteCookies = () => {
   try {
      deletePixelCookies(document, location.hostname);
   } catch {
      // Cookies are best effort; the library is already told to stop.
   }
};

const silence = () => {
   held = [];
   if (loadState === 'loading' || loadState === 'ready') {
      if (!revoked) callFbq('consent', 'revoke');
      revoked = true;
   }
   deleteCookies();
};

// The library keeps the first person it was given for a pixel id, so a different
// person in the same page load would be reported under the old identity. The pixel
// then stays off until the page reloads.
const syncLibraryUser = () => {
   if (loadState !== 'loading' && loadState !== 'ready') return;
   const externalId = user?.external_id ?? '';
   if (libraryExternalId && externalId !== libraryExternalId) {
      identityStale = true;
      held = [];
      return;
   }
   if (!user) return;
   const key = `${user.em ?? ''}:${user.external_id}`;
   if (key === userKey) return;
   userKey = key;
   libraryExternalId = user.external_id;
   callFbq(...initArgs());
};

// The one switch. Whoever knows the person's choice calls it: the signed-out consent
// check and the signed-in check. The script loads after the first `true`.
// Google Tag Manager follows the same choice.
export const setPixelAllowed = (next: boolean) => {
   setGtmAllowed(next && !!pixelId() && !isMinorStopped());
   paused = false;
   if (!pixelId()) {
      allowed = false;
      return;
   }
   if (next && !isMinorStopped()) {
      allowed = true;
      if (loadState === 'idle') startLoad();
      else if (revoked) {
         callFbq('consent', 'grant');
         revoked = false;
      }
      syncLibraryUser();
      flushHeld();
      return;
   }
   allowed = false;
   silence();
};

// A different account signed in and its choice is not known yet. Nothing is sent until
// setPixelAllowed answers, and the browser id stays in case that answer is yes.
// Events held for an account that has just been replaced are dropped.
export const pausePixel = (dropHeld = false) => {
   pauseGtm(dropHeld);
   paused = true;
   if (dropHeld) held = [];
};

// Someone said they are under 18. The pixel stops for this browser, survives a
// reload, and ignores any later setPixelAllowed(true).
export const stopPixelForMinor = () => {
   stopGtmForMinor();
   minorStopped = true;
   try {
      localStorage.setItem(MINOR_KEY, '1');
   } catch {
      // The in-memory flag covers this page load.
   }
   allowed = false;
   silence();
};

type SendOptions = { eventID?: string; onSent?: () => void; key?: string };

const send = (name: string, params: unknown, options: SendOptions = {}): boolean => {
   const rule = Object.hasOwn(PIXEL_EVENTS, name) ? PIXEL_EVENTS[name] : undefined;
   const clean = sanitisePixelParams(name, params);
   if (!rule || !clean) return false;
   // Before the person has said yes nothing is kept, so a yes never reaches back.
   if (!isActive()) return false;
   loadSent();
   const key = options.key ?? (options.eventID ? `id:${options.eventID}` : rule.once ? `event:${name}` : '');
   const item: Item = { name, params: clean, eventID: options.eventID, key, onSent: options.onSent };
   if (key && sentKeys.has(key)) {
      // Fired earlier but the server was not told: tell it again, send nothing.
      if (options.eventID) options.onSent?.();
      return false;
   }
   if (loadState === 'failed') return false;
   if (loadState !== 'ready' || !addressIsClean()) {
      if (rule.hold && !(key && held.some((entry) => entry.key === key)) && held.length < HELD_LIMIT) held.push(item);
      return false;
   }
   return emit(item);
};

// One onboarding question, at most once per journey. Returns whether the library took it.
export const trackStep = (step: PixelStep | string): boolean => {
   if (!(PIXEL_STEPS as readonly string[]).includes(step)) return false;
   return send(pixelStepEventName(step as PixelStep), {}, { key: `step:${step}` });
};

// A fixed-name event. Unknown names and stray parameters are refused. The optional
// callback runs only once the library has really taken the event.
export const trackEvent = (
   name: string,
   params: Record<string, unknown> = {},
   eventID?: string,
   onSent?: () => void,
): boolean => send(name, params, { eventID, onSent });

// For a call made while the router is still moving: the library reads the address
// when it fires, so the event waits until the new page is the current one.
export const trackEventAfterNavigation = (name: string, params: Record<string, unknown> = {}) => {
   try {
      useNuxtApp().hooks.hookOnce('page:finish', () => {
         trackEvent(name, params);
      });
   } catch {
      trackEvent(name, params);
   }
};

// Called after the route settles and when the address changes.
export const flushHeldPixelEvents = flushHeld;

// Called once for each navigation that finished, after setPixelRouteParams. A page
// that is not clean gets no view, now or later. A navigation that only changed the
// query of the same page (a page tidying its own address) is the same visit: it
// moves a view still waiting to the new address, and never sends a second one.
export const notePixelPageView = (samePage = false) => {
   if (!samePage) pageViewSent = false;
   pageView = !pageViewSent && addressIsClean() ? currentAddress() : null;
   sendPageView();
};

export const setPixelRouteParams = (hasParams: boolean) => {
   routeHasParams = hasParams;
};

// Advanced matching: the hashed email and account id, handed to the library once
// the person is signed in. Nothing else about the person is ever sent.
export const setPixelUser = (next: PixelUser | null) => {
   user = next && next.external_id ? next : null;
   if (user && isActive()) syncLibraryUser();
};

export const identifyPixelUser = async (email: unknown, accountId: unknown, fallbackEmailHash = '') => {
   const [em, external_id] = await Promise.all([pixelEmailHash(email), pixelExternalIdHash(accountId)]);
   setPixelUser(external_id ? { em: em || fallbackEmailHash || undefined, external_id } : null);
};

export const clearPixelUser = () => {
   user = null;
   userKey = '';
};

// The Meta browser id for the checkout request: only for someone currently allowed.
export const pixelFbp = (): string => (isActive() ? readPixelFbp(document.cookie) : '');

export const pixelIsEnabled = () => !!pixelId();

export const useMetaPixel = () => ({
   setPixelAllowed,
   pausePixel,
   stopPixelForMinor,
   trackStep,
   trackEvent,
   trackEventAfterNavigation,
   flushHeldPixelEvents,
   notePixelPageView,
   setPixelRouteParams,
   setPixelUser,
   identifyPixelUser,
   clearPixelUser,
   pixelFbp,
   pixelIsEnabled,
});
